import { useContext, useMemo, useCallback } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { getEmployeeDisplayName } from '@ubeya/shared-web/utils/employee/utils';
import { TIMESHEET_GROUP_BY_OPTIONS_ENUM } from '@ubeya/shared/constants';
import { getLeafChildrenData } from 'timeSheet/utils';
import { NameAggregateRenderer, NameRenderer } from 'timeSheet/components/CellRenderers';
import { isProjectMultiDay } from 'scheduling/utils';

const useNameColumn = ({
  totalRows,
  groupBy,
  accountId,
  setSelectedEmployee,
  showCaptain,
  showRating,
  showAvatar = false
}) => {
  const { t, isRtl, isShowLastNameFirst } = useContext(ConfigContext);

  const comparator = useCallback(
    (valueA, valueB, nodeA, nodeB) => {
      const isGroupRow = nodeA?.group && nodeB?.group;

      if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.EMPLOYEE) {
        if (isGroupRow) {
          const groupItemsA = getLeafChildrenData(nodeA);
          const groupItemsB = getLeafChildrenData(nodeB);

          if (groupItemsA.length > 0 && groupItemsB.length > 0) {
            const employeeA = groupItemsA[0]?.employee;
            const employeeB = groupItemsB[0]?.employee;

            const nameA = getEmployeeDisplayName(employeeA?.firstName, employeeA?.lastName, isShowLastNameFirst);
            const nameB = getEmployeeDisplayName(employeeB?.firstName, employeeB?.lastName, isShowLastNameFirst);

            return nameA.localeCompare(nameB);
          }
        }
        return null;
      }
      if (isGroupRow) {
        return 0;
      }

      const employeeA = nodeA?.data?.employee;
      const employeeB = nodeB?.data?.employee;

      const nameA = getEmployeeDisplayName(employeeA?.firstName, employeeA?.lastName, isShowLastNameFirst);
      const nameB = getEmployeeDisplayName(employeeB?.firstName, employeeB?.lastName, isShowLastNameFirst);

      return nameA.localeCompare(nameB);
    },
    [groupBy, isShowLastNameFirst]
  );

  const valueGetter = useCallback(
    (params) => {
      if (groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.EMPLOYEE) {
        return params.data?.scheduleIn;
      }
      const employee = params.data?.employee;
      return getEmployeeDisplayName(employee?.firstName, employee?.lastName, isShowLastNameFirst);
    },
    [groupBy, isShowLastNameFirst]
  );

  const cellRenderer = useCallback(
    (params) => {
      if (params.node?.group) {
        const groupItems = getLeafChildrenData(params.node);

        if (groupItems.length === 0) {
          return null;
        }

        return NameAggregateRenderer({
          rowData: {
            summarized: false,
            totalRows,
            subRows: groupItems,
            groupValue: params.node.key,
            isMultiDay: isProjectMultiDay({
              date: groupItems[0].projectStartDate,
              endDate: groupItems[0].projectEndDate
            })
          },
          columnData: { groupBy, onClickEmployee: setSelectedEmployee },
          isExpended: params.node.expanded
        });
      }

      if (params.data?.summarized) {
        return NameAggregateRenderer({
          rowData: params.data,
          columnData: { groupBy, onClickEmployee: setSelectedEmployee },
          isExpended: false
        });
      }

      return NameRenderer({
        rowData: params.data,
        columnData: {
          groupBy,
          accountId,
          onClickEmployee: setSelectedEmployee,
          showCaptain,
          showRating,
          showAvatar
        }
      });
    },
    [totalRows, groupBy, accountId, setSelectedEmployee, showCaptain, showRating, showAvatar]
  );

  return useMemo(
    () => ({
      field: 'name',
      headerName: t('name'),
      pinned: isRtl ? 'right' : 'left',
      width: 250,
      suppressMovable: true,
      cellClass: 'justify-start flex',
      suppressColumnsToolPanel: true,
      comparator,
      valueGetter,
      cellRenderer
    }),
    [t, comparator, valueGetter, cellRenderer, isRtl]
  );
};

export default useNameColumn;
